import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Grid, Box, Stack, Typography, Chip, CircularProgress, Backdrop, TextField, MenuItem , useTheme, List, ListItem, ListItemText, ListItemSecondaryAction, Tooltip, IconButton, Divider } from "@mui/material";
import { useAuth } from "../../../../provider";
import SectionLabvisitGroups from "../ReuseComps/SectionLabVisitGroups";
import * as c from '../../../../constants';
import Summary from "./Summary";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import AMHChart from "../Fertility/AMHChart";
import HormoneCycleChart from "../BloodHormones/HormoneCycleChart";
import MenopauseHormoneChart from "../BloodHormones/MenopauseHormoneChart";
import { useTranslation } from "../../../../language";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from "@mui/icons-material/Download";
import { Route, Droplet } from 'lucide-react';

/*
selectedDate
   ↓
filteredMeasurements
   ↓
historyMap
   ↓
groupedMarkers
   ↓
overallAnalysis
   ↓
UI (cards, charts, dialogs)
*/

// ---------------------------------------------------------------------------
// Helper functions for pdf download
// ---------------------------------------------------------------------------
function getFilenameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "report.pdf";
  } catch {
    return "report.pdf";
  }
}

function formatDate(isoString) {
  return new Date(isoString).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function handleDownload(url) {
  const filename = getFilenameFromUrl(url);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";          // fallback if download is blocked cross-origin
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}


export default function DateController({ category }) {

  const theme = useTheme()
  const { t } = useTranslation()
  const { state } = useAuth();
  const userId = state.user.id;
  const sex = state.user.gender;

  const url_usermeasurements = `${c.API_URL}/api/user/allmeasurements/${category}/`;
  const url_cycle = `${c.API_URL}/api/cycles/by-date/`;
  const url_pdf = `${c.API_URL}/api/labreports/my/`;

  const [measurements, setMeasurements] = useState([]);
  const [measureloading, setMeasureloading] = useState(false);
  const [error, setError] = useState(null);

  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

// ── pdf download ───────────────────────────────────────────────
    const [pdfloading, setPdfloading] = useState(false);
    const [reports, setReports] = useState([]);
    const [open, setOpen] = useState(false);

  const [cycleDay, setCycleDay] = useState(null);
  const [cyclePhase, setCyclePhase] = useState(null);

  // ── Marker Status Summary  ───────────────────────────────────────────────
  const [openStatusDialog, setOpenStatusDialog] = useState(null); // "orange" | "red" | null

// ── pdf Download ────────────────────────────────────────────────────────
useEffect(() => {
    axios
      .get(url_pdf)
      .then((res) => setReports(res.data))
      .catch(console.error)
      .finally(() => setPdfloading(false));
  }, []);

  /* -------------------- CYCLE DATA - only female -------------------- */

  useEffect(() => {
    if (!selectedDate || sex === "M") return;
    const fetchCycle = async () => {
      try {
        const res = await axios.get(`${url_cycle}?target_date=${selectedDate}`);
        setCycleDay(res.data.cycle_day ?? null);
        setCyclePhase(res.data.cycle_phase ?? null);
      } catch {
        setCycleDay(null);
        setCyclePhase(null);
      }
    };
    fetchCycle();
  }, [selectedDate]);

// ── Fetch all user measurements per category (from props)  ───────────────────────────────────────────────

  useEffect(() => {
    let isMounted = true; // prevents state update on unmounted component

    const fetchMeasurements = async () => {
      setMeasureloading(true);
      try {
        const response = await axios.get(url_usermeasurements); 

        if (!isMounted) return;
        const data = response.data;
        setMeasurements(data);

       // 1. Extract all dates
        const allDates = data.map((item) => item.date);
        // 2. Get unique dates
        // 3. Sort dates (latest first)
        const sortedDates = [...new Set(data.map(d => d.date))]
          .sort((a, b) => new Date(b) - new Date(a));

        setDates(sortedDates);

        if (sortedDates.length > 0) {
          setSelectedDate(sortedDates[0]);
        }

      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setMeasureloading(false);
        }
      }
    };

    fetchMeasurements();

    return () => {
      isMounted = false;
    };
  }, []);


/* --------------------  Filter measurements by selectedDate -------------------- */
const filteredMeasurements = useMemo(() => {
  if (!measurements?.length || !selectedDate) return [];

  return measurements.filter((m) => m.date === selectedDate);
}, [measurements, selectedDate]);


// ── Analyse green, orange, red markers - numbers ───────────────────────────────────────────────
const overallAnalysis = useMemo(() => {
  if (!filteredMeasurements?.length) return null;

  return filteredMeasurements.reduce(
    (acc, item) => {
      const status = item.status;

      if (status === "green") acc.green++;
      if (status === "orange") {
        acc.orange++;
        acc.orange_markers.push(item.reference?.marker?.name);
      }
      if (status === "red") {
        acc.red++;
        acc.red_markers.push(item.reference?.marker?.name);
      }

      return acc;
    },
    {
      green: 0,
      orange: 0,
      red: 0,
      orange_markers: [],
      red_markers: [],
    }
  );
}, [filteredMeasurements]);


// ── Build grouped structure from filtered data  ───────────────────────────────────────────────
const historyMap = useMemo(() => {
  const map = {};

  for (const m of measurements) {
    const id = m.reference?.marker?.id;
    if (!id) continue;

    if (!map[id]) map[id] = [];
    map[id].push(m);
  }

  for (const id in map) {
    map[id].sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  return map;
}, [measurements]);

const groupedMarkers = useMemo(() => {
  if (!filteredMeasurements.length) return {};

  const map = {};

  for (const m of filteredMeasurements) {
    const marker = m.reference?.marker;
    if (!marker) continue;

    const system = marker.healthsystem;
    const subsystem = marker.healthsubsystem;

    if (!map[system]) map[system] = {};
    if (!map[system][subsystem]) map[system][subsystem] = [];

    map[system][subsystem].push({
      ...marker,
      value: m.value,
      status: m.status,
      unit: m.unit,
      reference: m.reference,
      date: m.date,
      history: historyMap[marker.id] || []
    });
  }

  // sort markers inside subsystem
  Object.keys(map).forEach(system => {
    Object.keys(map[system]).forEach(sub => {
      map[system][sub].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
  });

  return map;
}, [filteredMeasurements, historyMap]);

const amhMeasurement = useMemo(() => {
  return filteredMeasurements.find(
    (m) => m.reference?.marker?.short === "Amh"
  );
}, [filteredMeasurements]);

// Extract the 4 hormone values for HormoneCycleChart (only relevant for category=hormones)
const HORMONE_ALIASES = {
  E2:  ['e2', 'estradiol', 'oestradiol'],
  P4:  ['p4', 'progesterone', 'prog'],
  LH:  ['lh'],
  FSH: ['fsh'],
};

const hormoneValues = useMemo(() => {
  if (category !== 'hormones') return null;
  const result = {};
  for (const [key, aliases] of Object.entries(HORMONE_ALIASES)) {
    const m = filteredMeasurements.find((m) => {
      const short = (m.reference?.marker?.short ?? '').toLowerCase();
      const name  = (m.reference?.marker?.name  ?? '').toLowerCase();
      return aliases.some(a => short.includes(a) || name.includes(a));
    });
    if (m) result[key] = parseFloat(m.value);
  }
  return Object.keys(result).length ? result : null;
}, [filteredMeasurements, category]);

const MENOPAUSE_ALIASES = {
  fsh: ['fsh'],
  lh:  ['lh'],
  e2:  ['e2', 'estradiol', 'oestradiol'],
  p4:  ['p4', 'progesterone', 'prog'],
};

const menopauseMarkers = useMemo(() => {
  if (category !== 'hormones') return {};
  const result = {};
  for (const [key, aliases] of Object.entries(MENOPAUSE_ALIASES)) {
    const m = filteredMeasurements.find((m) => {
      const short = (m.reference?.marker?.short ?? '').toLowerCase();
      const name  = (m.reference?.marker?.name  ?? '').toLowerCase();
      return aliases.some(a => short.includes(a) || name.includes(a));
    });
    if (m) result[key] = { value: parseFloat(m.value), unit: m.unit, status: m.status };
  }
  return result;
}, [filteredMeasurements, category]);

/* -------------------- LOADING -------------------- */

  if (measureloading) {return ( <Backdrop open> <CircularProgress /> </Backdrop> );}
  if (error) return <Typography color="error">Error loading data</Typography>;

/* -------------------- HANDLE DATE CHANGE -------------------- */
const handleDateChange = (e) => { 
  setSelectedDate(e.target.value);
};

return (
<>

<Grid container sx={{ mb: 4, display: "flex", justifyContent: "center", alignItems: "flex-start", width: { xs: "100%", sm: "75%", lg: "55%", xl: "43%" } }}>

{/* Intro Text - pdf Download */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>

        <Typography variant="subtitle1" textAlign="center">
            {t("blood_intro1")} <br />
            {t("blood_intro2")} <br />

            {t("blood_pdf")} &nbsp;&nbsp;
                 <Tooltip title="Download PDF Reports" placement="right">
                  <span>
                    <IconButton
                      onClick={() => setOpen(true)}
                      disabled={reports.length === 0}
                      color="secondary"
                      aria-label="open pdf reports"
                    >
                      <PictureAsPdfIcon sx={{fontSize: '1.7rem', color: 'text.secondary', "&:hover": { transform: "scale(1.2)" },}} />
                    </IconButton>
                  </span>
                </Tooltip>
          </Typography>
        </Grid>

  {/* Select Labvisit Date */}
      <Grid size={12} sx={{mt:4, display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <Box width={200}>
          <TextField
            select
            label="Select Labvisit Date"
            size="small"
            fullWidth
            value={selectedDate || ""}
            onChange={handleDateChange} 
          >
            {dates.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Grid>

{/* Status summary chips */}
       <Typography variant="subtitle1" sx={{mt:4, fontWeight: 400, textAlign: "center"}}> Summary Marker </Typography>
        {overallAnalysis && (
          <Grid
            size={12}
            sx={{
              mt: 2,
              mb: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            
            {[
              { status: "green", label: "NORMAL", color: theme.palette.traffic.green },
              { status: "orange", label: "SUFFICIENT", color: theme.palette.traffic.orange },
              { status: "red", label: "OUT OF RANGE", color: theme.palette.traffic.red },
            ].map(({ status, label, color }) => {
              const clickable = status === "orange" || status === "red";

              return (
                <Chip
                  key={status}
                  onClick={
                    clickable ? () => setOpenStatusDialog(status) : undefined
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: "common.white" }}>
                        {overallAnalysis[status]}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: "common.white", opacity: 0.85 }}>
                        {label}
                      </Typography>
                    </Box>
                  }
                  size="medium"
                  sx={{
                    bgcolor: color,
                    px: 0.5,
                    cursor: clickable ? "pointer" : "default",
                    "& .MuiChip-label": { px: 1 },
                    ...(clickable && {
                      "&:hover": { filter: "brightness(0.88)" },
                    }),
                  }}
                />
              );
            })}
          </Grid>
        )}

{/* Conditional: Only Female --> Cycle Info */}
 {/* Female Cycle Info - Medical Grade UI */}
        {sex === "F" && (
          <Grid size={12} sx={{ display: "flex", justifyContent: "center",  }}>
            <Box sx={{ p: 2.5}}>
              <Typography variant="subtitle1" sx={{fontWeight: 400, mb: 2, textAlign: "center"}}> Menstrual Cycle Status  </Typography>
              {/* Cycle Day Card */}
              <Stack direction="row" spacing={3}>
              <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5,  py: 0.6,borderRadius: 7, bgcolor: theme.palette.mode === "light" ? "primary.dark" : "primary.light",}}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.mode === "light" ? "common.white" : "common.black" }}> <Droplet size={14} /> &nbsp;&nbsp;CYCLE DAY: &nbsp;&nbsp;</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 400, color: theme.palette.mode === "light" ? "common.white" : "common.black"}} >  {cycleDay} </Typography>
              </Box>
              {/* Cycle Phase Card */}
              <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 0.6, borderRadius: 7, bgcolor: theme.palette.mode === "light" ? "primary.dark" : "primary.light", }} >
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.mode === "light" ? "common.white" : "common.black" }}><Route size={14} /> &nbsp;&nbsp;PHASE: &nbsp;&nbsp;</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 400, textTransform: "capitalize", color: theme.palette.mode === "light" ? "common.white" : "common.black" }} > {cyclePhase}</Typography>
              </Box>
           </Stack>
            </Box>
          </Grid>
        )}
        
{/* Sub-System & Marker Cards Mapping Sections */}
           {Object.entries(groupedMarkers).map(([system, subsystems]) => (
              <Grid
                size={12}
                key={system}
                sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}
              >
                <SectionLabvisitGroups
                  system={system}
                  subsystems={subsystems}
                  selectedDate={selectedDate}
                  userId = {userId}
                />
              </Grid>
            ))}
                  
 {/* Hormones and Cycle chart — only for hormones category, female */}
        {category === 'hormones' && sex === 'F' && selectedDate && (
          <Grid
            size={12}
            sx={{ mt: 4, width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Box sx={{ width: "100%" }}>
              <HormoneCycleChart
                latestValues={hormoneValues}
                cycleDay={cycleDay}
              />
            </Box>
          </Grid>
        )}

 {/* Menopause Hormone chart — only for hormones category, female */}
        {category === 'hormones' && sex === 'F' && selectedDate && (
          <Grid
            size={12}
            sx={{ mt: 4, width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Box sx={{ width: "100%" }}>
              <MenopauseHormoneChart
                markers={menopauseMarkers}
                age={state.user.age}
              />
            </Box>
          </Grid>
        )}

 {/* Conditional: Only Female --> AMH Chart Display */}
        {selectedDate && sex === "F" && category === "fertility_female" && amhMeasurement && (
            <Grid
              size={12}
              sx={{
                mt: 6,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
                <AMHChart
                  amh_value={Number(amhMeasurement.value)}
                  date={selectedDate}
                />
            </Grid>
          )}


{/* ── orange and red markers - dialog ────────────────────────────── */}

      <CustomDialogSmall
        open={Boolean(openStatusDialog)}
        onClose={() => setOpenStatusDialog(null)}
        title={
          openStatusDialog === "red"
            ? "Out of Range Markers"
            : "Sufficient Markers"
        }
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {(() => {
              const isRed = openStatusDialog === "red";

              const markers = isRed
                ? overallAnalysis?.red_markers
                : overallAnalysis?.orange_markers;

              if (!markers?.length) {
                return (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No markers in this category.
                  </Typography>
                );
              }

              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {markers.map((m, i) => (
                    <Chip
                      key={`${m}-${i}`}
                      label={m}
                      size="medium"
                      sx={{
                        fontWeight: 600,
                        bgcolor: isRed
                          ? theme.palette.traffic.red
                          : theme.palette.traffic.orange,
                        color: "white",
                      }}
                    />
                  ))}
                </Box>
              );
            })()}
          </Box>
        }
      />


{/* ── pdf download dialog ────────────────────────────── */}

     <CustomDialogSmall
          open={open}
          onClose={() => setOpen(false)}
          title="PDF Reports Download"
          content={
            !reports || reports.length === 0 ? (
              <Typography sx={{ p: 3 }} color="text.secondary">
                No reports available.
              </Typography>
            ) : (
              <List disablePadding>
                {reports.map((visit, visitIndex) => (
                  <Box key={visit.lab_visit_id}>
                    
                    {/* ── Visit Header ───────────────────────── */}
                    <Box sx={{ px: 2, py: 1.5, bgcolor: theme.palette.mode === "dark" ? "action.hover" : "grey.100" }}>
                      <Typography variant="subtitle2">
                        {visit.lab_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(visit.visit_date)}
                      </Typography>
                    </Box>

                    {/* ── Reports inside visit ───────────────── */}
                    {visit.reports.map((report, reportIndex) => (
                      <Box key={report.id}>
                        <ListItem sx={{ pr: 8, py: 1.5 }}>
                          <ListItemText
                            primary={report.description}
                            secondary={formatDate(report.uploaded_at)}
                            primaryTypographyProps={{
                              variant: "body1",
                              noWrap: true,
                              title: report.description,
                            }}
                            secondaryTypographyProps={{
                              variant: "body2",
                            }}
                          />

                          <ListItemSecondaryAction>
                            <Tooltip title="Download PDF">
                              <IconButton
                                edge="end"
                                onClick={() => handleDownload(report.file_url)}
                                color="secondary"
                                aria-label={`download ${report.description}`}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>

                        {/* Divider between reports */}
                        {reportIndex < visit.reports.length - 1 && (
                          <Divider component="li" />
                        )}
                      </Box>
                    ))}

                    {/* Divider between visits */}
                    {visitIndex < reports.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </List>
            )
          }
        />

</Grid>

</>
  );
}